// =====================================================================
// LOCATION PERMISSIONS MANAGER - ADMIN PANEL COMPONENT
// =====================================================================
// Purpose: Admin UI for managing granular user location permissions
// Features: 3-level permissions (view, publish, cross-publish) per location
// =====================================================================

import React, { useState, useEffect } from 'react'
import './LocationPermissionsManager.css'

interface User {
  id: string
  username: string
  email: string
  firstname: string
  lastname: string
  home_location: string
  role: string
  status: string
  is_active: boolean
  created_at: string
  permissions: LocationPermission[]
}

interface LocationPermission {
  location: string
  can_view: boolean
  can_publish: boolean
  can_cross_publish: boolean
  granted_at: string
  granted_by: string | null
  notes: string | null
}

interface PermissionStats {
  by_location: {
    location: string
    total_users: number
    can_publish_count: number
    can_view_count: number
    home_location_users: number
  }[]
  cross_location_users: number
}

interface AuditLogEntry {
  id: number
  user_id: string
  location: string
  action: string
  old_can_publish: boolean | null
  new_can_publish: boolean | null
  changed_at: string
  reason: string | null
  target_username: string
  changed_by_username: string | null
}

interface LocationPermissionsManagerProps {
  onClose?: () => void
}

// =====================================================================
// PERMISSION BADGE HELPERS
// =====================================================================

// Determine permission level based on flags
const getPermissionLevel = (perm: LocationPermission | null): {
  level: 'full' | 'publish' | 'view' | 'none';
  color: string;
  emoji: string;
  label: string;
} => {
  if (!perm || !perm.can_view) {
    return { level: 'none', color: '#9ca3af', emoji: '⚪', label: 'No Access' };
  }
  if (perm.can_view && perm.can_publish && perm.can_cross_publish) {
    return { level: 'full', color: '#10b981', emoji: '🟢', label: 'Full Access' };
  }
  if (perm.can_view && perm.can_publish) {
    return { level: 'publish', color: '#f59e0b', emoji: '🟡', label: 'Publish' };
  }
  return { level: 'view', color: '#3b82f6', emoji: '🔵', label: 'View Only' };
};

// Permission Badge Component
const PermissionBadge: React.FC<{
  permission: LocationPermission | null;
  onClick?: () => void;
}> = ({ permission, onClick }) => {
  const { color, emoji, label} = getPermissionLevel(permission);
  
  return (
    <div 
      className="permission-badge" 
      style={{ 
        backgroundColor: `${color}15`,
        border: `2px solid ${color}`,
        color: color,
        padding: '0.5rem 0.75rem',
        borderRadius: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        cursor: onClick ? 'pointer' : 'default',
        fontSize: '13px',
        fontWeight: '600',
        transition: 'all 0.2s'
      }}
      onClick={onClick}
      title={permission ? `View: ${permission.can_view ? '✓' : '✗'} | Publish: ${permission.can_publish ? '✓' : '✗'} | Cross: ${permission.can_cross_publish ? '✓' : '✗'}` : 'No permissions set'}
    >
      <span style={{ fontSize: '14px' }}>{emoji}</span>
      <span>{label}</span>
    </div>
  );
};

export const LocationPermissionsManager: React.FC<LocationPermissionsManagerProps> = ({ onClose }) => {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [stats, setStats] = useState<PermissionStats | null>(null)
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([])
  const [availableLocations, setAvailableLocations] = useState<string[]>([])
  
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'users' | 'stats' | 'audit'>('users')
  const [viewMode, setViewMode] = useState<'table' | 'matrix'>('table')
  const [searchQuery, setSearchQuery] = useState('')
  const [locationFilter, setLocationFilter] = useState<'ALL' | string>('ALL')
  
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  })

  const token = localStorage.getItem('egl_token')

  // Fetch users with permissions
  const fetchUsers = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/admin/location-permissions/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      
      if (data.success) {
        setUsers(data.data.users)
        setFilteredUsers(data.data.users)
        
        // Extract unique locations from all users
        const locations = new Set<string>()
        data.data.users.forEach((user: User) => {
          locations.add(user.home_location)
          user.permissions?.forEach(p => locations.add(p.location))
        })
        setAvailableLocations(Array.from(locations).sort())
      } else {
        showToast(data.message || 'Failed to fetch users', 'error')
      }
    } catch (error) {
      showToast('Error fetching users', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/location-permissions/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      
      if (data.success) {
        setStats(data.data)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  // Fetch audit log
  const fetchAuditLog = async () => {
    try {
      const response = await fetch('/api/admin/location-permissions/audit?limit=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      
      if (data.success) {
        setAuditLog(data.data.audit_log)
      }
    } catch (error) {
      console.error('Error fetching audit log:', error)
    }
  }

  // Show toast notification
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ show: true, message, type })
    
    if (type === 'success') {
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000)
    }
  }

  // Toggle permission (view, publish, cross-publish)
  const togglePermission = async (
    userId: string, 
    location: string, 
    permissionType: 'can_view' | 'can_publish' | 'can_cross_publish',
    currentValue: boolean
  ) => {
    try {
      // Get current permissions for this location
      const user = users.find(u => u.id === userId)
      const currentPerm = user?.permissions?.find(p => p.location === location)
      
      const updatedPermission = {
        location,
        can_view: currentPerm?.can_view ?? true,
        can_publish: currentPerm?.can_publish ?? false,
        can_cross_publish: currentPerm?.can_cross_publish ?? false,
        notes: `Updated by admin`
      }
      
      // Toggle the specific permission
      updatedPermission[permissionType] = !currentValue
      
      // Send update to backend
      const response = await fetch(`/api/admin/location-permissions/users/${userId}/grant`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedPermission)
      })
      
      const data = await response.json()
      
      if (data.success) {
        showToast(`✅ ${permissionType} ${!currentValue ? 'enabled' : 'disabled'} for ${user?.username} @ ${location}`, 'success')
        fetchUsers()
        fetchStats()
      } else {
        showToast(data.message || 'Failed to update permission', 'error')
      }
    } catch (error) {
      showToast('Error updating permission', 'error')
    }
  }

  // Initial load
  useEffect(() => {
    fetchUsers()
    fetchStats()
    fetchAuditLog()
  }, [])

  // Apply filters
  useEffect(() => {
    let filtered = users

    if (searchQuery) {
      filtered = filtered.filter(u => 
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    if (locationFilter !== 'ALL') {
      filtered = filtered.filter(u => u.home_location === locationFilter)
    }

    setFilteredUsers(filtered)
  }, [searchQuery, locationFilter, users])

  // Get permission for user-location pair
  const getPermission = (user: User, location: string): LocationPermission | null => {
    return user.permissions?.find(p => p.location === location) || null
  }

  // Helper to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="location-perms-manager">
      {/* Header */}
      <div className="perms-header">
        <div className="perms-title">
          <h2>🌍 Location Permissions Management</h2>
          <p className="perms-subtitle">
            Manage user access to location-based features
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="perms-tabs">
        <button 
          className={`perms-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users ({users.length})
        </button>
        <button 
          className={`perms-tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          📊 Statistics
        </button>
        <button 
          className={`perms-tab ${activeTab === 'audit' ? 'active' : ''}`}
          onClick={() => setActiveTab('audit')}
        >
          📜 Audit Log
        </button>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`perms-toast perms-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Content */}
      <div className="perms-content">
        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div className="perms-users-tab">
            {/* View Mode Toggle + Filters */}
            <div className="perms-filters">
              {/* View Mode Toggle */}
              <div className="view-mode-toggle" style={{ display: 'flex', gap: '0.5rem', marginRight: '1rem' }}>
                <button
                  className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                  onClick={() => setViewMode('table')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: viewMode === 'table' ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                    background: viewMode === 'table' ? '#eff6ff' : 'white',
                    color: viewMode === 'table' ? '#3b82f6' : '#6b7280',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'all 0.2s'
                  }}
                >
                  📋 Table View
                </button>
                <button
                  className={`view-mode-btn ${viewMode === 'matrix' ? 'active' : ''}`}
                  onClick={() => setViewMode('matrix')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: viewMode === 'matrix' ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                    background: viewMode === 'matrix' ? '#eff6ff' : 'white',
                    color: viewMode === 'matrix' ? '#3b82f6' : '#6b7280',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'all 0.2s'
                  }}
                >
                  🗂️ Matrix View
                </button>
              </div>
              
              <input 
                type="text" 
                placeholder="🔍 Search by username or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="perms-search"
              />
              <select 
                value={locationFilter} 
                onChange={(e) => setLocationFilter(e.target.value)}
                className="perms-filter-select"
              >
                <option value="ALL">All Locations</option>
                {availableLocations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            {/* Permission Matrix Table or Matrix View */}
            {isLoading ? (
              <div className="perms-loading">Loading users...</div>
            ) : viewMode === 'matrix' ? (
              /* MATRIX VIEW - Simplified Layout */
              <div className="perms-matrix-view" style={{ overflowX: 'auto' }}>
                <table className="matrix-simple-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #e5e7eb' }}>Username</th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid #e5e7eb' }}>Home</th>
                      {availableLocations.map(location => (
                        <th key={location} style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid #e5e7eb' }}>
                          {location}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <span style={{ fontWeight: '600', color: '#1f2937' }}>{user.username}</span>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>{user.email}</span>
                          </div>
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <span style={{ 
                            padding: '0.25rem 0.75rem', 
                            background: '#f3f4f6', 
                            borderRadius: '6px', 
                            fontSize: '12px', 
                            fontWeight: '600',
                            color: '#4b5563'
                          }}>
                            {user.home_location}
                          </span>
                        </td>
                        {availableLocations.map(location => {
                          const perm = getPermission(user, location);
                          return (
                            <td key={location} style={{ padding: '1rem', textAlign: 'center' }}>
                              <PermissionBadge permission={perm} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* ORIGINAL TABLE VIEW - Unchanged */
              <div className="perms-table-wrapper">
                <table className="perms-matrix-table">
                  <thead>
                    <tr>
                      <th className="user-col">User</th>
                      <th className="home-col">Home</th>
                      {availableLocations.map(location => (
                        <th key={location} className="location-col">
                          <div className="location-header">
                            <span className="location-name">{location}</span>
                            <div className="permission-labels">
                              <span className="perm-label" title="Can view certificates from this location">👁️</span>
                              <span className="perm-label" title="Can see and use Publish to ODBC button">📤</span>
                              <span className="perm-label" title="Can cross-publish other location certificates to this ODBC">🔄</span>
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => {
                      const perm = getPermission(user, location)
                      return (
                        <tr key={user.id}>
                          <td className="user-col">
                            <div className="user-info">
                              <span className="username">{user.username}</span>
                              <span className="email">{user.email}</span>
                            </div>
                          </td>
                          <td className="home-col">
                            <span className="home-badge">{user.home_location}</span>
                          </td>
                          {availableLocations.map(location => {
                            const perm = getPermission(user, location)
                            const isHome = user.home_location === location
                            const isAdmin = user.role === 'ADMIN'
                            const shouldLock = isHome && !isAdmin  // Don't lock for admin users
                            
                            return (
                              <td key={location} className="permission-col">
                                {/* Color-coded badge summary */}
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <PermissionBadge permission={perm} />
                                </div>
                                
                                <div className="permission-toggles">
                                  {/* View Permission */}
                                  <button
                                    className={`perm-toggle ${perm?.can_view ? 'active' : ''} ${shouldLock ? 'locked' : ''}`}
                                    onClick={() => !shouldLock && togglePermission(user.id, location, 'can_view', perm?.can_view ?? false)}
                                    disabled={shouldLock}
                                    title={shouldLock ? 'Always enabled for home location' : `${perm?.can_view ? 'Disable' : 'Enable'} view permission`}
                                  >
                                    {perm?.can_view ? '✅' : '❌'}
                                  </button>
                                  
                                  {/* Publish Permission */}
                                  <button
                                    className={`perm-toggle ${perm?.can_publish ? 'active' : ''} ${shouldLock ? 'locked' : ''}`}
                                    onClick={() => !shouldLock && togglePermission(user.id, location, 'can_publish', perm?.can_publish ?? false)}
                                    disabled={shouldLock}
                                    title={shouldLock ? 'Always enabled for home location' : `${perm?.can_publish ? 'Disable' : 'Enable'} publish button access`}
                                  >
                                    {perm?.can_publish ? '✅' : '❌'}
                                  </button>
                                  
                                  {/* Cross-Publish Permission */}
                                  <button
                                    className={`perm-toggle ${perm?.can_cross_publish ? 'active' : ''}`}
                                    onClick={() => togglePermission(user.id, location, 'can_cross_publish', perm?.can_cross_publish ?? false)}
                                    title={`${perm?.can_cross_publish ? 'Disable' : 'Enable'} cross-publish to ${location} ODBC`}
                                  >
                                    {perm?.can_cross_publish ? '✅' : '❌'}
                                  </button>
                                </div>
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Legend */}
            <div className="perms-legend">
              <h3>Permission Types</h3>
              <div className="legend-items">
                <div className="legend-item">
                  <span className="legend-icon">👁️</span>
                  <div>
                    <strong>View Permission</strong>
                    <p>User can VIEW certificates created in this location in the certificate list</p>
                  </div>
                </div>
                <div className="legend-item">
                  <span className="legend-icon">📤</span>
                  <div>
                    <strong>Publish Button Access</strong>
                    <p>User can SEE and CLICK the "[Location]: Publish to ODBC" button</p>
                  </div>
                </div>
                <div className="legend-item">
                  <span className="legend-icon">🔄</span>
                  <div>
                    <strong>Cross-Publish Permission</strong>
                    <p>User can publish certificates created in OTHER locations to THIS location's ODBC table</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STATISTICS TAB */}
        {activeTab === 'stats' && stats && (
          <div className="perms-stats-tab">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-header">🌍 Cross-Location Users</div>
                <div className="stat-value">{stats.cross_location_users}</div>
                <div className="stat-description">Users with publish access to multiple locations</div>
              </div>

              {stats.by_location.map(loc => (
                <div key={loc.location} className="stat-card">
                  <div className="stat-header">📍 {loc.location}</div>
                  <div className="stat-details">
                    <div className="stat-row">
                      <span>Home Users:</span>
                      <strong>{loc.home_location_users}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Can View:</span>
                      <strong>{loc.can_view_count}</strong>
                    </div>
                    <div className="stat-row">
                      <span>Can Publish:</span>
                      <strong>{loc.can_publish_count}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AUDIT LOG TAB */}
        {activeTab === 'audit' && (
          <div className="perms-audit-tab">
            <div className="audit-table-wrapper">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Location</th>
                    <th>Action</th>
                    <th>Change</th>
                    <th>Changed By</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map(entry => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.changed_at)}</td>
                      <td>{entry.target_username}</td>
                      <td><span className="location-badge">{entry.location}</span></td>
                      <td><span className={`action-badge action-${entry.action.toLowerCase()}`}>{entry.action}</span></td>
                      <td>
                        {entry.old_can_publish === null ? '—' : entry.old_can_publish ? '✅' : '❌'} 
                        {' → '}
                        {entry.new_can_publish === null ? '—' : entry.new_can_publish ? '✅' : '❌'}
                      </td>
                      <td>{entry.changed_by_username || 'System'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
